import { useDroppable } from '@dnd-kit/core';
import clsx from 'clsx';
import { useAtom, useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';
import { GridSize, MAX_MOUNTED_TABS } from '../util';
import MultiCamHeader from './header/MultiCamHeader';
import MultiCamTabGrid from './grid/MultiCamTabGrid';
import {
  DROP_ZONE_ID,
  activeTabAtom,
  gridSizeOverrideAtom,
  tabsAtom,
} from '../state';

interface Props {
  isFullScreen: boolean;
  onToggleFullScreen: () => void;
}

export function MultiCamDetails(props: Props) {
  const { isFullScreen, onToggleFullScreen } = props;
  const tabs = useAtomValue(tabsAtom);
  const activeTab = useAtomValue(activeTabAtom);
  const [gridSizeOverride, setGridSizeOverride] = useAtom(gridSizeOverrideAtom);
  const activeTabId = activeTab?.id ?? null;

  const { setNodeRef, isOver } = useDroppable({ id: DROP_ZONE_ID });

  // Ids of tabs kept mounted, most-recently-viewed first.
  const [mountedTabIds, setMountedTabIds] = useState<string[]>([]);

  const onChangeGridSize = (size: GridSize | null) => {
    if (!activeTabId) return;
    setGridSizeOverride((prev) => ({ ...prev, [activeTabId]: size }));
  };

  // Promote the active tab to the front of the mounted set, capped at N.
  useEffect(() => {
    if (!activeTabId) return;
    setMountedTabIds((prev) => {
      if (prev[0] === activeTabId) return prev;
      return [activeTabId, ...prev.filter((id) => id !== activeTabId)].slice(
        0,
        MAX_MOUNTED_TABS
      );
    });
  }, [activeTabId]);

  // Drop ids of tabs that no longer exist.
  useEffect(() => {
    setMountedTabIds((prev) => {
      const next = prev.filter((id) => tabs.some((tab) => tab.id === id));
      return next.length === prev.length ? prev : next;
    });
  }, [tabs]);

  return (
    <div
      ref={setNodeRef}
      className={clsx(
        'flex h-full w-full flex-col',
        isOver && 'ring-primary-500 rounded-lg ring-2 ring-inset'
      )}
    >
      {tabs.length === 0 ? (
        <div
          className={clsx(
            'dark:border-ondark-line-2 dark:text-ondark-text-2 border-ondark-text-2 text-onlight-text-3 m-4 flex flex-1 items-center justify-center border-2 border-dashed',
            isOver && 'border-primary-500 text-primary-500'
          )}
        >
          <div>Drop or select cameras to view them here</div>
        </div>
      ) : (
        <>
          <MultiCamHeader
            gridSize={
              activeTabId ? gridSizeOverride[activeTabId] ?? null : null
            }
            onChangeGridSize={onChangeGridSize}
            isFullScreen={isFullScreen}
            onToggleFullScreen={onToggleFullScreen}
          />
          <div className="relative min-h-0 min-w-0 flex-1 overflow-hidden">
            {tabs
              .filter(
                (tab) =>
                  tab.id === activeTabId || mountedTabIds.includes(tab.id)
              )
              .map((tab) => (
                <div
                  key={tab.id}
                  className={clsx(
                    'absolute inset-4',
                    tab.id !== activeTabId && 'pointer-events-none invisible'
                  )}
                >
                  <MultiCamTabGrid
                    tab={tab}
                    isActive={tab.id === activeTabId}
                  />
                </div>
              ))}
          </div>
        </>
      )}
    </div>
  );
}

export default MultiCamDetails;
